import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import BrandTabsModal from 'components/brands-filter/BrandTabsModal';
import Spinner from 'components/spinner/Spinner';
import withInfluencerMeta from 'components/brands-filter/WithInfluencerMeta';

import InfluencerHistoryItem from '../item/TileStandardFooterHistoryItem';
import { ModalFrameFooter, ModalFrameMessage } from 'components/modal';

import { fetchInfluencerCampaignHistory, resetInfluencerCampaignHistory } from 'modules/influencers/reducers/influencerCampaignHistory';

import { getEntityFromProfile } from 'utils/view';
import { __t, applyTranslations } from 'utils/language';
import { BRAND_FILTER_QUERY_PARAM } from 'utils/brands';

import './tile-standard-footer-history-modal.scss';

import type {
  BrandProfile,
  InfluencerCampaignHistory,
  InfluencerMeta,
  InfluencerProfile,
  Organization
} from 'types';

applyTranslations(require('../../tile-standard-footer.i18n.json'));

type OwnProps = {
  influencerProfile: InfluencerProfile,
  influencerMeta: InfluencerMeta,
  onBeforeCloseModal: () => void
};

type StateProps = {
  filter: string,
  campaignHistory: InfluencerCampaignHistory,
  campaignHistoryLoading: boolean,
  currentOrganization: Organization,
  mapByOrganizationId: {
    [x: number]: Organization
  }
};

type DispatchProps = {
  dispatchResetInfluencerCampaignHistory: () => void,
  dispatchFetchInfluencerCampaignHistory: (c: number, b: number, a?: string | null) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const MSG_KEY = 'tile_standard_footer';

class TileStandardFooterHistoryModal extends React.PureComponent<Props> {
  componentDidMount(): void {
    this.loadHistory(this.props.filter);
  }

  componentDidUpdate(prevProps) {
    if (this.props.filter !== prevProps.filter) {
      this.loadHistory(this.props.filter);
    }
  }

  componentWillUnmount(): void {
    const { dispatchResetInfluencerCampaignHistory } = this.props;
    dispatchResetInfluencerCampaignHistory();
  }

  loadHistory = (filter: string): void => {
    const { currentOrganization, influencerProfile, dispatchFetchInfluencerCampaignHistory } = this.props;
    dispatchFetchInfluencerCampaignHistory(currentOrganization.id, influencerProfile.id, filter);
  };

  getHistoryLink() {
    const { filter, influencerProfile } = this.props;
    const entity = getEntityFromProfile(influencerProfile);

    return (entity && entity.url)
      ? `${entity.url}?${BRAND_FILTER_QUERY_PARAM}=${filter}#history`
      : '';
  }

  isDisabled(brandProfile: BrandProfile): boolean {
    const { mapByOrganizationId } = this.props;
    if (!brandProfile || !brandProfile.organizationId) {
      return false;
    }

    return !mapByOrganizationId[brandProfile.organizationId];
  }

  renderCampaignHistory(): React.ReactNode {
    const { campaignHistory, filter } = this.props;

    return (
      <React.Fragment>
        {
          isEmpty(campaignHistory) && (
            <ModalFrameMessage
              message={__t(`${MSG_KEY}.messages.no_history_${filter}`)}
            />
          )
        }
        {
          (campaignHistory || []).map(item => (
            <div
              className="tile-standard-footer-history-modal__item"
              key={`tile-standard-footer-history-modal-${item.subcampaign.id}`}
            >
              <InfluencerHistoryItem
                item={item}
                disabled={this.isDisabled(item.campaign.brandProfile)}
              />
            </div>
          ))
        }
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { campaignHistoryLoading, influencerProfile, influencerMeta, onBeforeCloseModal } = this.props;
    const historyLink = this.getHistoryLink();

    return (
      <BrandTabsModal
        title={__t(`${MSG_KEY}.labels.work_history`)}
        influencerProfile={influencerProfile}
        influencerMeta={influencerMeta}
        metaKey="history"
        hideCount
      >
        {
          campaignHistoryLoading ?
            <Spinner
              position="inline"
              size="small"
              color="light"
            /> :
            this.renderCampaignHistory()
        }
        <ModalFrameFooter>
          <ActionButton
            key={`tile-standard-footer-history-modal__view-button-${historyLink}`}
            color="secondary"
            newTab
            link={historyLink}
          >
            {__t(`${MSG_KEY}.buttons.visit_campaign_history`)}
          </ActionButton>
          <ActionButton
            color="secondary"
            onClick={onBeforeCloseModal}
          >
            {__t(`${MSG_KEY}.buttons.done`)}
          </ActionButton>
        </ModalFrameFooter>
      </BrandTabsModal>
    );
  }
}

const mapStateToProps = ({ brandsFilter, organizations, influencerCampaignHistory }): StateProps => ({
  filter: brandsFilter.activeFilter,
  currentOrganization: organizations.currentOrganization,
  campaignHistory: influencerCampaignHistory.list,
  campaignHistoryLoading: influencerCampaignHistory.isLoading,
  mapByOrganizationId: organizations.mapByOrganizationId
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetInfluencerCampaignHistory: () => dispatch(resetInfluencerCampaignHistory()),
  dispatchFetchInfluencerCampaignHistory: (currentOrganizationId, profileId, filter) => dispatch(fetchInfluencerCampaignHistory(currentOrganizationId, profileId, { filter }))
});

const EnhancedTileStandardFooterHistoryModal = withInfluencerMeta()(TileStandardFooterHistoryModal);

export default connect(mapStateToProps, mapDispatchToProps)(EnhancedTileStandardFooterHistoryModal);
